import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { apiFetch, ApiError } from '../lib/api';
import type { CaseTimelineEventSummary } from '../types';

const COLOR_PRESETS = [
  '#185FA5',
  '#1A5C3A',
  '#0F6E56',
  '#7A4A00',
  '#2E7DD4',
  '#3A1A6E',
  '#8B1A1A',
];

export function CaseTimelineEventModal({
  caseId,
  initial,
  onSaved,
  onClose,
  onDeleted,
}: {
  caseId: string;
  initial?: CaseTimelineEventSummary | null;
  onSaved: () => void;
  onClose: () => void;
  onDeleted?: () => void;
}) {
  const { t } = useTranslation(['cases', 'common']);
  const editing = !!initial;

  const [title, setTitle] = useState(initial?.title ?? '');
  const [description, setDescription] = useState(initial?.description ?? '');
  const [eventAt, setEventAt] = useState(() =>
    toDateInput(initial?.eventAt ?? new Date().toISOString()),
  );
  const [color, setColor] = useState(initial?.color ?? COLOR_PRESETS[0]!);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const body = {
        title: title.trim(),
        description: description.trim() || null,
        eventAt: new Date(eventAt).toISOString(),
        color,
      };
      if (editing && initial) {
        await apiFetch(`/api/cases/${caseId}/timeline/${initial.id}`, {
          method: 'PATCH',
          body: JSON.stringify(body),
        });
      } else {
        await apiFetch(`/api/cases/${caseId}/timeline`, {
          method: 'POST',
          body: JSON.stringify(body),
        });
      }
      onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.code : err instanceof Error ? err.message : 'unknown');
    } finally {
      setBusy(false);
    }
  }

  async function handleDelete() {
    if (!editing || !initial) return;
    if (!confirm(t('timeline_modal.delete_confirm'))) return;
    setBusy(true);
    try {
      await apiFetch(`/api/cases/${caseId}/timeline/${initial.id}`, { method: 'DELETE' });
      onDeleted?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'delete_failed');
      setBusy(false);
    }
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="form-card modal-card" onClick={(e) => e.stopPropagation()}>
        <form onSubmit={handleSubmit}>
          <div className="form-card-header">
            <div style={{ flex: 1 }}>
              <div className="form-preview-name">
                {editing ? t('timeline_modal.title_edit') : t('timeline_modal.title_new')}
              </div>
            </div>
            <button
              type="button"
              className="modal-close-btn"
              onClick={onClose}
              aria-label={t('common:actions.close')}
            >
              ×
            </button>
          </div>

          <div className="form-section">
            <div className="field-grid">
              <div className="field" style={{ gridColumn: '1 / -1' }}>
                <label>{t('timeline_modal.field_title')} <span className="req">*</span></label>
                <input value={title} onChange={(e) => setTitle(e.target.value)} required autoFocus />
              </div>
              <div className="field" style={{ gridColumn: '1 / -1' }}>
                <label>{t('timeline_modal.field_description')}</label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                />
              </div>
              <div className="field">
                <label>{t('timeline_modal.field_event_at')} <span className="req">*</span></label>
                <input
                  type="date"
                  value={eventAt}
                  onChange={(e) => setEventAt(e.target.value)}
                  required
                />
              </div>
              <div className="field">
                <label>{t('timeline_modal.field_color')}</label>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 4 }}>
                  {COLOR_PRESETS.map((c) => (
                    <button
                      key={c}
                      type="button"
                      onClick={() => setColor(c)}
                      aria-label={c}
                      style={{
                        width: 26,
                        height: 26,
                        borderRadius: '50%',
                        background: c,
                        border:
                          color === c ? '2px solid var(--text)' : '0.5px solid var(--rule2)',
                        cursor: 'pointer',
                        padding: 0,
                      }}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>

          {error && (
            <div className="form-section">
              <div className="signin-error">{error}</div>
            </div>
          )}

          <div className="form-footer">
            <button type="submit" className="btn-save" disabled={busy}>
              {busy ? t('common:actions.loading_dots') : t('common:actions.save')}
            </button>
            <button type="button" className="btn-cancel" onClick={onClose} disabled={busy}>
              {t('common:actions.cancel')}
            </button>
            {editing && onDeleted && (
              <button
                type="button"
                onClick={handleDelete}
                disabled={busy}
                className="btn-danger"
                style={{ marginInlineStart: 'auto' }}
              >
                {t('common:actions.delete')}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}

function toDateInput(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}
